/* eslint-disable @typescript-eslint/naming-convention */
import { useCallback, useContext, useMemo } from "react";

// Context
import { AnalyticsContext } from "@swiggy-private/checkout-core";

// types
import { ItemNudgeComms, PlanDetails, UserSuperStatus } from "@swiggy-private/cart-types";
import { CTATypes } from "@swiggy-private/super-ui";

// constants
import { PLAN_ADDITION_SOURCE } from "@cart-client/constants/index";
import { CLICK_ACTIONS, IMPRESSION_ACTIONS, SCREEN_NAME } from "@cart-client/analytics/constants";

interface useOneMembershipNudgeAnalyticsProps {
    itemNudgeComms: ItemNudgeComms | null;
    planDetails: PlanDetails;
    source: PLAN_ADDITION_SOURCE;
    userSuperStatus: UserSuperStatus;
}

export const useOneMembershipNudgeAnalytics = ({
    itemNudgeComms,
    userSuperStatus,
    planDetails,
    source,
}: useOneMembershipNudgeAnalyticsProps) => {
    const { clickEvent, impressionEvent } = useContext(AnalyticsContext);

    // Compute context value for analytics events
    const contextValue = useMemo(() => {
        const viewAllBenefitsCTA = itemNudgeComms?.ctas?.[0];
        const oneTapAddCTA = itemNudgeComms?.ctas?.[1];
        const changePlanCta = itemNudgeComms?.ctas?.[2];

        return JSON.stringify({
            header: itemNudgeComms?.title,
            maincopy: viewAllBenefitsCTA?.text,
            planprice: planDetails?.final_price,
            pricing: itemNudgeComms?.note,
            planid: planDetails?.plan_id,
            cta_text: changePlanCta?.text || oneTapAddCTA?.text,
            plan_added: oneTapAddCTA?.text === CTATypes.ONE_TAP_REMOVE,
            source,
            userSuperStatus,
        });
    }, [
        itemNudgeComms?.ctas,
        itemNudgeComms?.note,
        itemNudgeComms?.title,
        planDetails?.final_price,
        planDetails?.plan_id,
        source,
        userSuperStatus,
    ]);

    const onImpressionOneMembershipNudge = useCallback(
        () =>
            impressionEvent({
                category: SCREEN_NAME.CHECKOUT,
                action: IMPRESSION_ACTIONS.IMPRESSION_ONE_CARTUPSELL_NUDGE,
                context: contextValue,
            }),
        [contextValue, impressionEvent],
    );

    const onImpressionChangePlanCta = useCallback(
        () =>
            impressionEvent({
                category: SCREEN_NAME.CHECKOUT,
                action: IMPRESSION_ACTIONS.IMPRESSION_ONE_CARTUPSELL_CHANGE_PLAN_CTA,
                context: contextValue,
            }),
        [contextValue, impressionEvent],
    );

    const onClickChangePlanCta = useCallback(
        () =>
            clickEvent({
                category: SCREEN_NAME.CHECKOUT,
                action: CLICK_ACTIONS.CLICK_CHANGE_PLAN_CTA,
                context: contextValue,
            }),
        [clickEvent, contextValue],
    );

    const onClickViewAllBenefits = useCallback(
        () =>
            clickEvent({
                category: SCREEN_NAME.CHECKOUT,
                action: CLICK_ACTIONS.CLICK_ONE_CARTUPSELL_HALFCARD_CHEVRON,
                context: contextValue,
            }),
        [clickEvent, contextValue],
    );

    const onClickAddPlanCta = useCallback(
        () =>
            clickEvent({
                category: SCREEN_NAME.CHECKOUT,
                action: CLICK_ACTIONS.CLICK_ADD_CTA_BUTTON_ONE_CARTUPSELL,
                context: contextValue,
            }),
        [clickEvent, contextValue],
    );

    const onClickRemovePlanCta = useCallback(
        () =>
            clickEvent({
                category: SCREEN_NAME.CHECKOUT,
                action: CLICK_ACTIONS.CLICK_REMOVE_PLAN_ONE_CARTUPSELL,
                context: contextValue,
            }),
        [clickEvent, contextValue],
    );

    // Plan removal halfcard

    const onImpressionPlanRemovalHalfcard = useCallback(
        (context: string) =>
            impressionEvent({
                category: SCREEN_NAME.CHECKOUT,
                action: IMPRESSION_ACTIONS.IMPRESSION_ONE_CARTUPSELL_PLAN_REMOVAL_HALFCARD,
                context,
            }),
        [impressionEvent],
    );

    const onClickRemovePlanRemovalHalfcard = useCallback(
        (context: string) =>
            clickEvent({
                category: SCREEN_NAME.CHECKOUT,
                action: CLICK_ACTIONS.CLICK_ONE_CARTUPSELL_PLAN_REMOVAL_HALFCARD,
                context,
            }),
        [clickEvent],
    );

    return {
        onImpressionOneMembershipNudge,
        onClickViewAllBenefits,
        onClickAddPlanCta,
        onClickRemovePlanCta,
        onImpressionPlanRemovalHalfcard,
        onClickRemovePlanRemovalHalfcard,
        onImpressionChangePlanCta,
        onClickChangePlanCta,
    };
};
